import type { Formatter, TranslateFn } from '@/lib/i18n'

/**
 * One line of a reason's evidence table (Stage 39).
 *
 * Defined in its own module so the pure explanation builders — scenarios 1–5
 * (`anomaly-explanation.ts`) and scenarios 6–7 (`cross-unit.reason-evidence.ts`) — can
 * produce rows without importing a React component, and so both surfaces are typed by the
 * SAME row shape rather than by two look-alike ones.
 *
 * `value` is always a finished string: the builders own every rounding, plural and date
 * decision, and the component renders exactly what it is handed.
 */
export interface EvidenceRow {
  /** Left column: what is being counted, in the analyst's words. */
  label: string
  /** Right column: the value, already formatted. */
  value: string
  /**
   * Stage 40 — the rows behind this number, when they can be enumerated.
   *
   * Present ONLY on aggregates computed over a concrete set of historical records. A
   * ratio, a median, a quartile or a percentile has no row set, so it carries no
   * drill-down — it gets a tooltip instead.
   */
  drilldown?: EvidenceDrilldown | null
  /** Explanation shown on hover for a value that is computed rather than counted. */
  tooltip?: string | null
}

/** What the UI needs to offer «Показать N примеров» and to fetch them lazily. */
export interface EvidenceDrilldown {
  /** Which evidence number this belongs to — the reason code on the 6–7 surface. */
  evidenceKey: string
  /** Semantics of the row set, for the caption and the tests. */
  datasetType: string
  /** Exact size of the FULL set. Never the number of rows fetched. */
  totalCount: number
  /** Hard cap; the request never asks for more and the server never returns more. */
  maxDisplayedItems: number
  title: string
  /** Where the rows come from — the UI never assembles the set itself. */
  endpoint: string
  selectionDescription: string
  /**
   * Предупреждение о том, чего в колонках списка НЕ видно.
   *
   * Нужно там, где отбор идёт по полю, которого нет среди колонок примера: без него
   * заголовок обещает совпадение, которое читатель в таблице не найдёт и решит, что
   * показаны чужие строки. Текст не заменяет отбор и ничего не фильтрует.
   */
  columnsNote?: string | null
}

/** Server and client agree on one cap; neither side may raise it. */
export const MAX_EVIDENCE_EXAMPLES = 50

/**
 * A table is shown only when it carries at least this many rows.
 *
 * One row is not evidence — it is a number without a comparison, which is exactly the
 * shape the quantitative work replaced. Below the threshold the reason states honestly
 * that the snapshot has too little to explain it.
 */
export const MIN_EVIDENCE_ROWS = 2

/** Rows with an absent or non-numeric-looking value never reach the table. */
export function isMeaningfulValue(value: unknown): boolean {
  if (value === null || value === undefined) return false
  const s = String(value).trim()
  if (!s) return false
  return !['nan', 'null', 'undefined', '—', '-', 'нет данных'].includes(s.toLowerCase())
}

/** Adds a row only when the value is meaningful — the hide-empty rule in one place. */
export function pushEvidence(
  rows: EvidenceRow[],
  label: string,
  value: string | null | undefined,
  extra?: { drilldown?: EvidenceDrilldown | null; tooltip?: string | null },
): void {
  if (!isMeaningfulValue(value)) return
  rows.push({ label, value: String(value).trim(), ...(extra ?? {}) })
}

/**
 * «Показать 18 связок» / «Показать 50 примеров из 72».
 *
 * The second form exists so a truncated list can never read as the whole set — the count
 * the user sees is always the FULL one, with the sample size named separately.
 *
 * Существительное приходит КЛЮЧОМ словаря (`analytics.evi.nounTransports` /
 * `analytics.evi.nounVariants`), а не тройкой русских форм: в казахском форма одна,
 * и захардкоженная тройка выдала бы русские слова в казахском интерфейсе.
 * Форму выбирает t() через Intl.PluralRules.
 */
export function drilldownButtonLabel(
  d: EvidenceDrilldown,
  nounKey: string,
  t: TranslateFn,
  f: Formatter,
): string {
  return d.totalCount <= d.maxDisplayedItems
    ? t('analytics.evi.showAll', {
        count: f.int(d.totalCount),
        noun: t(nounKey, { count: d.totalCount }),
      })
    : t('analytics.evi.showSample', {
        shown: d.maxDisplayedItems,
        total: f.int(d.totalCount),
      })
}

/** «Показано 50 из 119 связок, использованных при расчёте этого показателя.» */
export function drilldownFootnote(
  d: EvidenceDrilldown,
  shown: number,
  nounKey: string,
  t: TranslateFn,
  f: Formatter,
): string {
  return drilldownFootnoteText(d, shown, t(nounKey, { count: d.totalCount }), t, f)
}

/**
 * Та же подпись, но существительное приходит ГОТОВЫМ словом, а не ключом словаря.
 *
 * Нужно там, где единица счёта определяется формой пришедших строк, а не панелью:
 * у сценариев 1–5 строка — связка «импорт → экспорт», у 6–7 — одна нога корпуса,
 * и панель одна на обе поверхности (см. cross-unit/components/evidence-examples.tsx
 * и ai_transit/v2/reports/ANOMALY_EVIDENCE_TERMINOLOGY_AUDIT.md). Ключ словаря там
 * выбрать нечем: он один на панель, а слово — на строку.
 */
export function drilldownFootnoteText(
  d: EvidenceDrilldown,
  shown: number,
  noun: string,
  t: TranslateFn,
  f: Formatter,
): string {
  if (d.totalCount <= d.maxDisplayedItems) {
    return t('analytics.evi.footnoteAll', { count: f.int(d.totalCount), noun })
  }
  return t('analytics.evi.footnoteSample', { shown, total: f.int(d.totalCount), noun })
}
